/**
 * Pure list reducers for the optimistic department mutations.
 *
 * `useDepartments` holds the department array; every optimistic update and
 * every rollback in `DepartmentsTab` runs through one of the functions here,
 * which keeps the state transitions testable without React and makes the
 * rollback guarantees checkable in isolation (Req 4.5, 5.5, 6.9, 6.12).
 *
 * Contract shared by every function in this module:
 *   - pure: no React, no API calls, no module-level state,
 *   - never mutates the input array, the department objects inside it, or the
 *     `patch` argument — a changed department is rebuilt with a spread,
 *   - always returns a new array, including on the no-op paths (id not found,
 *     zero delta, malformed arguments), so callers can pass the result
 *     straight to a setState without aliasing the previous state,
 *   - tolerates a non-array `departments` argument by treating it as empty,
 *     mirroring the defensive reads in `departmentScopes.js` (Req 10.7).
 *
 * Ids are matched on the first department only. The backend keys departments
 * uniquely, but matching one entry is what makes the rollback round-trips
 * exact: a remove that dropped every match could not be undone by a single
 * re-insert, and a patch applied to every match could not be undone by a
 * single pre-submit snapshot.
 */

/**
 * Returns the given value as an array, or an empty array when it is not one.
 *
 * @param {*} departments
 * @returns {Array<*>}
 */
function toList(departments) {
  return Array.isArray(departments) ? departments : [];
}

/**
 * Returns true when a department entry carries the given id.
 *
 * Matching is strict first, then falls back to comparing string forms so an
 * id read from a DOM value (`"3"`) still matches a numeric payload id (`3`).
 * Entries without an id never match, so a missing id cannot be found by
 * passing `undefined`.
 *
 * @param {*} department
 * @param {*} id
 * @returns {boolean}
 */
function matchesId(department, id) {
  if (!department || typeof department !== "object") return false;
  if (department.id === undefined || department.id === null) return false;
  if (department.id === id) return true;
  return String(department.id) === String(id);
}

/**
 * Reads a department's member count defensively. A missing, null, or
 * non-finite `member_count` reads as `0` rather than propagating `NaN` into
 * the rendered table.
 *
 * @param {*} department
 * @returns {number} A finite, non-negative-safe base count.
 */
function readMemberCount(department) {
  if (!department || typeof department !== "object") return 0;
  const count = department.member_count;
  if (typeof count !== "number" || !Number.isFinite(count)) return 0;
  return count;
}

/**
 * Finds the position of a department by id.
 *
 * Used by the delete path to record a row's index before removing it, so a
 * failed delete can restore the row where it was rather than appending it
 * (Req 5.5).
 *
 * @param {*} departments - The department list, or anything else.
 * @param {*} id - The department id to look for.
 * @returns {number} The index of the first match, or `-1` when there is none.
 */
export function findDepartmentIndex(departments, id) {
  if (id === undefined || id === null) return -1;
  return toList(departments).findIndex((department) =>
    matchesId(department, id)
  );
}

/**
 * Inserts a department at a specific position.
 *
 * The index is clamped to `[0, length]`, so a stale or out-of-range index
 * appends or prepends instead of producing a sparse array; a non-numeric
 * index appends. Passing `departments.length` (or the index recorded before a
 * remove) round-trips a removed row back into place.
 *
 * @param {*} departments - The department list, or anything else.
 * @param {*} department - The department to insert, used as given.
 * @param {number} index - The target position.
 * @returns {Array<*>} A new array with the department inserted.
 */
export function insertDepartmentAt(departments, department, index) {
  const list = toList(departments);

  const requested =
    typeof index === "number" && Number.isFinite(index)
      ? Math.trunc(index)
      : list.length;
  const position = Math.max(0, Math.min(list.length, requested));

  return [...list.slice(0, position), department, ...list.slice(position)];
}

/**
 * Merges a patch into one department.
 *
 * The patched department is a new object (`{ ...department, ...patch }`), so
 * the previous object stays intact and can be reused as the pre-submit
 * snapshot for a rollback (Req 4.5). Restoring that snapshot is exact for the
 * fields the dialog edits, since the patch only ever overwrites keys the
 * snapshot already carries. An id that is not present, or a patch that is not
 * a plain object, yields an unchanged copy of the list.
 *
 * @param {*} departments - The department list, or anything else.
 * @param {*} id - The id of the department to patch.
 * @param {*} patch - The fields to overwrite.
 * @returns {Array<*>} A new array with the matched department replaced.
 */
export function replaceDepartment(departments, id, patch) {
  const list = toList(departments);
  const index = findDepartmentIndex(list, id);

  if (index === -1) return list.slice();
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    return list.slice();
  }

  const next = list.slice();
  next[index] = { ...next[index], ...patch };
  return next;
}

/**
 * Removes one department by id.
 *
 * Only the first match is dropped, so pairing this with
 * `insertDepartmentAt(list, snapshot, recordedIndex)` restores the original
 * list including its order (Req 5.5). An id that is not present yields an
 * unchanged copy.
 *
 * @param {*} departments - The department list, or anything else.
 * @param {*} id - The id of the department to remove.
 * @returns {Array<*>} A new array without the matched department.
 */
export function removeDepartment(departments, id) {
  const list = toList(departments);
  const index = findDepartmentIndex(list, id);

  if (index === -1) return list.slice();
  return [...list.slice(0, index), ...list.slice(index + 1)];
}

/**
 * Adjusts a department's member count by a signed delta.
 *
 * Deltas rather than snapshots, because `MembersView` only disables the
 * controls for the in-flight member email (Req 6.13): two assignments for
 * different emails can overlap, and a snapshot restore would clobber the
 * concurrent adjustment. Delta arithmetic composes in any order, so a
 * rollback applies the inverse delta (Req 6.9, 6.12).
 *
 * The resulting count is clamped at zero, so a stale rollback arriving after
 * the count has already dropped cannot render a negative member count. The
 * inverse-delta round trip is therefore exact whenever the intermediate
 * result did not clamp — guaranteed for the `±1` deltas the UI issues against
 * a count that already includes the member being removed.
 *
 * A delta of `0`, a non-numeric delta, or an id that is not present leaves
 * every department object untouched; the count is only normalized to a number
 * on the entry actually being adjusted.
 *
 * @param {*} departments - The department list, or anything else.
 * @param {*} id - The id of the department whose count changes.
 * @param {number} delta - The signed adjustment, typically `+1` or `-1`.
 * @returns {Array<*>} A new array with the adjusted department replaced.
 */
export function applyMemberCountDelta(departments, id, delta) {
  const list = toList(departments);

  if (typeof delta !== "number" || !Number.isFinite(delta) || delta === 0) {
    return list.slice();
  }

  const index = findDepartmentIndex(list, id);
  if (index === -1) return list.slice();

  const current = list[index];
  const next = list.slice();
  next[index] = {
    ...current,
    member_count: Math.max(0, readMemberCount(current) + delta),
  };
  return next;
}
